import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ProductBadge } from "../components/Badges";
import { PRODUCT_TYPES } from "../lib/workflows";
import { toast } from "sonner";
import { UploadCloud, FileText, Sparkles, Lock, CheckCircle2, Mail, Paperclip, Loader2, Radio, Plus, History, Download, Trash2, Activity, ShieldAlert, ShieldCheck, AlertTriangle, RefreshCw } from "lucide-react";

const PRODUCTS = PRODUCT_TYPES;

export default function Intake() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState(null);
  const [creating, setCreating] = useState(false);
  const [inbox, setInbox] = useState([]);
  const [processingId, setProcessingId] = useState(null);
  const [simulating, setSimulating] = useState(false);
  const [mailbox, setMailbox] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [forceReprocess, setForceReprocess] = useState(false);
  const [reprocessDays, setReprocessDays] = useState(1);
  const [reprocessInfo, setReprocessInfo] = useState(null);
  const [pulling, setPulling] = useState(false);
  const [gmail, setGmail] = useState(null);
  const [gmailSyncing, setGmailSyncing] = useState(false);
  const [ingest, setIngest] = useState(null);
  const [scrape, setScrape] = useState(null);

  const loadIngest = async () => {
    try { const { data } = await api.get("/admin/ingest-runs?limit=8"); setIngest(data); } catch {}
  };
  const loadScrape = async () => {
    try { const { data } = await api.get("/admin/scrape-health"); setScrape(data); } catch {}
  };
  useEffect(() => {
    if (!isAdmin) return;
    loadIngest(); loadScrape();
    const t = setInterval(() => { loadIngest(); loadScrape(); }, 20000);
    return () => clearInterval(t);
  }, [isAdmin]);

  const loadGmail = async () => {
    try { const { data } = await api.get("/gmail/status"); setGmail(data); } catch {}
  };
  useEffect(() => { loadGmail(); }, []);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get("gmail");
    if (!q) return;
    if (q === "connected") toast.success("Gmail connected — live sync is on");
    else if (q === "error") toast.error("Gmail connection failed. Check your Google credentials.");
    else if (q === "badstate") toast.error("Session expired — please try connecting again.");
    window.history.replaceState({}, "", "/intake");
    loadGmail();
  }, []);

  const connectGmail = async () => {
    try { const { data } = await api.get("/gmail/auth-url"); window.location.href = data.url; }
    catch (e) { toast.error(e.response?.data?.detail || "Gmail is not configured yet"); }
  };

  const syncGmail = async () => {
    setGmailSyncing(true);
    try { const { data } = await api.post("/gmail/sync"); toast.success(`Gmail synced — ${data.created} new order(s)`); loadInbox(); loadGmail(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gmail sync failed"); }
    finally { setGmailSyncing(false); }
  };

  const disconnectGmail = async () => {
    if (!window.confirm("Disconnect the Gmail inbox? Live auto-sync will stop.")) return;
    try { await api.post("/gmail/disconnect"); toast.success("Gmail disconnected"); loadGmail(); }
    catch { toast.error("Could not disconnect"); }
  };

  useEffect(() => { api.get("/mailbox/status").then((r) => setMailbox(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/mailbox/reprocess-status").then((r) => setReprocessInfo(r.data)).catch(() => {}); }, []);

  const syncMailbox = async () => {
    setSyncing(true);
    try { const { data } = await api.post("/mailbox/sync"); toast.success(data.message || "Mailbox sync started"); setTimeout(() => { loadInbox(); loadIngest(); }, 4000); }
    catch (e) { toast.error(e.response?.data?.detail || "Mailbox sync failed"); }
    finally { setSyncing(false); }
  };

  const pollReprocess = async (attempt = 0) => {
    try {
      const { data } = await api.get("/mailbox/reprocess-status");
      if (data.status === "running" && attempt < 90) {
        setTimeout(() => pollReprocess(attempt + 1), 4000);
        return;
      }
      if (data.status === "done") {
        setReprocessInfo(data);
        const recovered = data.updated || 0;
        const stillBlank = data.still_blank || 0;
        toast.success(
          recovered > 0
            ? `Force re-process complete — recovered ${recovered} order(s)${stillBlank ? `, ${stillBlank} still missing details` : ""}.`
            : `Force re-process complete — no new details found${stillBlank ? `; ${stillBlank} order(s) still blank` : ""}.`,
          { duration: 8000 }
        );
        loadInbox();
      } else if (data.status === "error") {
        toast.error("Force re-process hit an error — check the logs.");
      }
    } catch { /* stop polling on error */ }
  };

  const resyncMailbox = async () => {
    const span = reprocessDays <= 1 ? "today's" : `the last ${reprocessDays} days'`;
    const confirmMsg = forceReprocess
      ? `Re-scan the last 24 hours AND force AI re-processing of ${span} blank orders?\n\nThis re-extracts Borrower & Property details from the original emails for any orders still missing them (e.g. created while AI credits were at zero). Existing filled details are kept. This can take a few minutes.`
      : "Re-scan the last 24 hours of email? Existing orders are left untouched — only previously-missed orders are added. This can take a few minutes.";
    if (!window.confirm(confirmMsg)) return;
    setResyncing(true);
    try {
      const { data } = await api.post(`/mailbox/resync?days=1&clear=true&force_reprocess=${forceReprocess}&reprocess_days=${reprocessDays}`);
      toast.success(data.message || "Re-sync started");
      if (forceReprocess) setTimeout(() => pollReprocess(0), 4000);
      else setTimeout(loadInbox, 5000);
    }
    catch (e) { toast.error(e.response?.data?.detail || "Re-sync failed"); }
    finally { setResyncing(false); }
  };

  const pullOrder = async () => {
    const ref = window.prompt("Enter the order number to pull from the mailbox (e.g. YDL-2026-118):");
    if (!ref || !ref.trim()) return;
    setPulling(true);
    try { const { data } = await api.post(`/mailbox/fetch-order?q=${encodeURIComponent(ref.trim())}`); toast.success(data.message || "Searching mailbox…"); setTimeout(loadInbox, 6000); }
    catch (e) { toast.error(e.response?.data?.detail || "Pull failed"); }
    finally { setPulling(false); }
  };

  const clearMailStubs = async () => {
    if (!window.confirm("Remove stuck 'MAIL-' junk orders? This only deletes legacy MAIL- stubs and cannot be undone.")) return;
    try { const { data } = await api.post("/orders/cleanup-mail-stubs"); toast.success(`${data.deleted} MAIL- stub(s) removed`); }
    catch (e) { toast.error(e.response?.data?.detail || "Cleanup failed"); }
  };

  const loadInbox = async () => {
    try { const { data } = await api.get("/inbox"); setInbox(data); } catch {}
  };
  useEffect(() => {
    loadInbox();
    const t = setInterval(loadInbox, 15000);
    return () => clearInterval(t);
  }, []);

  const relTime = (iso) => {
    if (!iso) return "";
    const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const h = Math.floor(mins / 60);
    return h < 24 ? `${h}h ${mins % 60}m ago` : `${Math.floor(h / 24)}d ago`;
  };

  const simulateEmail = async () => {
    setSimulating(true);
    try {
      await api.post("/inbox/simulate");
      toast.success("New email received");
      loadInbox();
    } catch { toast.error("Could not fetch new email"); }
    finally { setSimulating(false); }
  };

  const processEmail = async (id) => {
    setProcessingId(id);
    try {
      const { data } = await api.post(`/inbox/${id}/process`);
      toast.success(data.already ? "Email already processed" : "Email parsed — order created");
      loadInbox();
      navigate(`/orders/${data.order.id}`);
    } catch { toast.error("Could not process email"); }
    finally { setProcessingId(null); }
  };

  const onFile = (f) => {
    if (!f) return;
    const ok = [".pdf", ".docx", ".txt", ".eml"].some((e) => f.name.toLowerCase().endsWith(e));
    if (!ok) { toast.error("Upload a PDF, DOCX, TXT or EML file"); return; }
    setFile(f);
    setResult(null);
    setForm(null);
    parse(f); // auto-run AI extraction the moment a package is dropped/selected
  };

  const parse = async (f) => {
    const theFile = f || file;
    if (!theFile) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", theFile);
      const { data } = await api.post("/intake/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      const p = data.parsed;
      setForm({
        order_ref: p.order_ref || "",
        property_address: p.property_address || "",
        state: p.state || "",
        county: p.county || "",
        zip_code: p.zip_code || "",
        borrower: p.borrower || "",
        parcel_id: p.parcel_id || "",
        company: "",
        product_type: p.product_type || "Full Search",
        source_group: "",
        client_name: p.client_name || "",
        client_last_name: p.client_last_name || "",
        client_email: p.client_email || "",
        client_phone: p.client_phone || "",
      });
      toast.success("Attachment parsed by AI");
    } catch (e) {
      toast.error("AI parsing failed");
    } finally {
      setParsing(false);
    }
  };

  const createOrder = async () => {
    if (!form.order_ref || !form.property_address) { toast.error("Order ID and Property Address are required"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/orders?source=email", form);
      toast.success("Order created");
      navigate(`/orders/${data.id}`);
    } catch { toast.error("Could not create order"); }
    finally { setCreating(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-700 tracking-tight text-slate-100">Email Intake &amp; AI Parsing</h1>
        <p className="text-sm text-slate-400 mt-1">The hub monitors the incoming email feed. Process a message to auto-extract Order ID, Property, Product Type &amp; Client — or upload an attachment manually.</p>
      </div>

      {/* Monitored email feed */}
      {/* Gmail live sync (recommended) */}
      <div className="rounded-lg border border-border bg-card p-5" data-testid="gmail-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-teal" />
            <h3 className="font-display text-lg font-600">Gmail Live Sync</h3>
            {gmail && (
              <span data-testid="gmail-status" className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${gmail.connected ? "text-emerald-300 bg-emerald-500/15 border-emerald-500/30" : "text-amber-300 bg-amber-500/15 border-amber-500/30"}`}>
                {gmail.connected ? `Connected: ${gmail.email}` : "Not connected"}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {gmail?.connected ? (
              <>
                <button data-testid="gmail-sync-btn" onClick={syncGmail} disabled={gmailSyncing}
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-navy-2 transition-colors disabled:opacity-50">
                  {gmailSyncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Radio className="h-3.5 w-3.5" />} Sync now
                </button>
                <button data-testid="gmail-disconnect-btn" onClick={disconnectGmail}
                  className="inline-flex items-center gap-1.5 rounded-md border border-white/15 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:bg-white/5 transition-colors">
                  Disconnect
                </button>
              </>
            ) : (
              <button data-testid="gmail-connect-btn" onClick={connectGmail} disabled={gmail && !gmail.oauth_configured}
                className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800 transition-colors disabled:opacity-50">
                <Mail className="h-3.5 w-3.5" /> Connect Gmail
              </button>
            )}
          </div>
        </div>
        <p className="text-xs text-slate-400 mt-2">
          {gmail?.connected
            ? "Every email delivered to the connected inbox (all 4 groups) is polled automatically every 15 minutes — attachments captured, parsed by AI and auto-assigned."
            : gmail && !gmail.oauth_configured
            ? "Gmail OAuth is not configured yet. Add GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET to enable one-click connect."
            : "Connect the intake@ inbox once — no App Password or IMAP needed. It securely reads new emails and turns them into live orders."}
        </p>
      </div>

      {/* Live mailbox */}
      <div className="rounded-lg border border-border bg-card p-5" data-testid="mailbox-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Radio className="h-4 w-4 text-teal" />
            <h3 className="font-display text-lg font-600">Live Mailbox</h3>
            {mailbox && (
              <span data-testid="mailbox-status" className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${mailbox.configured ? "text-emerald-300 bg-emerald-500/15 border-emerald-500/30" : "text-amber-300 bg-amber-500/15 border-amber-500/30"}`}>
                {mailbox.configured ? `${(mailbox.accounts || []).filter((a) => a.connected).length}/${(mailbox.accounts || []).length} connected` : "Not connected"}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button data-testid="mailbox-pull-order-btn" onClick={pullOrder} disabled={pulling || !(mailbox && mailbox.configured)}
              className="inline-flex items-center gap-1.5 rounded-md border border-white/15 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:bg-white/5 transition-colors disabled:opacity-50">
              {pulling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Pull order #
            </button>
            <button data-testid="mailbox-clear-mail-stubs-btn" onClick={clearMailStubs}
              className="inline-flex items-center gap-1.5 rounded-md border border-white/15 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:bg-white/5 transition-colors">
              <Trash2 className="h-3.5 w-3.5" /> Clear MAIL- stubs
            </button>
            <label data-testid="force-reprocess-toggle" title="Wipe blank Borrower/Property on recent orders and re-extract them with AI from the original email"
              className="inline-flex items-center gap-1.5 rounded-md border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-slate-300 cursor-pointer hover:bg-white/5 transition-colors select-none">
              <input type="checkbox" data-testid="force-reprocess-checkbox" checked={forceReprocess}
                onChange={(e) => setForceReprocess(e.target.checked)}
                className="h-3.5 w-3.5 accent-teal-500 cursor-pointer" />
              Force Re-process
            </label>
            {forceReprocess && (
              <select data-testid="reprocess-window-select" value={reprocessDays}
                onChange={(e) => setReprocessDays(Number(e.target.value))}
                className="rounded-md border border-white/15 bg-transparent px-2 py-1.5 text-xs font-semibold text-slate-300 focus:outline-none focus:border-teal-500">
                <option value={1} className="bg-slate-800">Today</option>
                <option value={3} className="bg-slate-800">Last 3 days</option>
                <option value={7} className="bg-slate-800">Last 7 days</option>
              </select>
            )}
            <button data-testid="mailbox-resync-btn" onClick={resyncMailbox} disabled={resyncing || syncing || !(mailbox && mailbox.configured)}
              className="inline-flex items-center gap-1.5 rounded-md border border-white/15 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:bg-white/5 transition-colors disabled:opacity-50">
              {resyncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <History className="h-3.5 w-3.5" />} Re-sync last 24h
            </button>
            <button data-testid="mailbox-sync-btn" onClick={syncMailbox} disabled={syncing || !(mailbox && mailbox.configured)}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-navy-2 transition-colors disabled:opacity-50">
              {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Radio className="h-3.5 w-3.5" />} Sync now
            </button>
          </div>
        </div>
        <div className="mt-3 space-y-1">
          {(mailbox?.accounts || []).map((a, i) => (
            <div key={i} data-testid={`mailbox-acct-${i}`} className="flex items-center justify-between gap-2 text-xs">
              <span className="font-mono text-slate-300 truncate">{a.user} · {a.folder}</span>
              <span className={`shrink-0 rounded-full border px-2 py-0.5 font-semibold ${a.connected ? "text-emerald-300 bg-emerald-500/15 border-emerald-500/30" : "text-amber-300 bg-amber-500/15 border-amber-500/30"}`}>
                {a.connected ? "Connected" : "Awaiting app password"}
              </span>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-2">
          {mailbox && mailbox.configured
            ? "Connected inboxes are polled automatically every 15 minutes — attachments captured and orders auto-assigned."
            : "Add each Gmail App Password into IMAP_ACCOUNTS in backend/.env (host imap.gmail.com), then click Sync now."}
        </p>
        {reprocessInfo && reprocessInfo.finished_at && reprocessInfo.status !== "idle" && (
          <div data-testid="reprocess-history" className="mt-2 flex items-center gap-1.5 rounded-md border border-teal-500/20 bg-teal-500/10 px-3 py-1.5 text-[11px] text-slate-300">
            <History className="h-3 w-3 text-teal-400 shrink-0" />
            <span>
              Last force re-process: <span className="font-semibold text-teal-300">recovered {reprocessInfo.updated || 0}</span> order(s)
              {reprocessInfo.still_blank ? <>, <span className="font-semibold text-amber-300">{reprocessInfo.still_blank} still blank</span></> : null}
              {" · "}{new Date(reprocessInfo.finished_at).toLocaleString()}
            </span>
          </div>
        )}
      </div>

      {isAdmin && (
        <div className="rounded-lg border border-border bg-card p-5" data-testid="ingestion-dashboard">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-teal" />
              <h3 className="font-display text-lg font-600">Ingestion Dashboard</h3>
              <span className="text-[11px] text-slate-400">last 24h</span>
            </div>
            <button data-testid="ingest-refresh-btn" onClick={loadIngest}
              className="inline-flex items-center gap-1.5 rounded-md border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-slate-300 hover:bg-white/5 transition-colors">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
          </div>
          {ingest ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                {[
                  { k: "candidates", label: "Scanned", color: "text-slate-200" },
                  { k: "deduped", label: "Deduped", color: "text-sky-300" },
                  { k: "fetched", label: "Bodies Pulled", color: "text-indigo-300" },
                  { k: "created", label: "Orders Created", color: "text-emerald-300" },
                  { k: "merged", label: "Merged", color: "text-teal-300" },
                  { k: "duplicate", label: "Duplicates", color: "text-slate-300" },
                  { k: "skipped", label: "Skipped", color: "text-amber-300" },
                  { k: "errors", label: "Errors", color: "text-rose-300" },
                ].map((c) => (
                  <div key={c.k} data-testid={`ingest-stat-${c.k}`} className="rounded-md border border-white/10 bg-white/5 px-3 py-2">
                    <div className={`text-xl font-700 ${c.color}`}>{(ingest.rollup_24h?.[c.k] ?? 0).toLocaleString()}</div>
                    <div className="text-[11px] text-slate-400">{c.label}</div>
                  </div>
                ))}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-slate-400 border-b border-white/10">
                      <th className="py-1.5 pr-3 font-600">Run</th>
                      <th className="py-1.5 pr-3 font-600">Trigger</th>
                      <th className="py-1.5 pr-3 font-600 text-right">Scanned</th>
                      <th className="py-1.5 pr-3 font-600 text-right">Deduped</th>
                      <th className="py-1.5 pr-3 font-600 text-right">Pulled</th>
                      <th className="py-1.5 pr-3 font-600 text-right">Created</th>
                      <th className="py-1.5 pr-3 font-600 text-right">Merged</th>
                      <th className="py-1.5 pr-3 font-600 text-right">Skipped</th>
                      <th className="py-1.5 font-600 text-right">Errors</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {(ingest.runs || []).length === 0 && (
                      <tr><td colSpan={9} className="py-4 text-center text-slate-400">No ingestion runs recorded yet — trigger a sync to populate this.</td></tr>
                    )}
                    {(ingest.runs || []).map((r, i) => (
                      <tr key={i} data-testid={`ingest-run-${i}`} className="text-slate-200">
                        <td className="py-1.5 pr-3 whitespace-nowrap text-slate-300">{relTime(r.at)}{r.resync ? " · resync" : ""}</td>
                        <td className="py-1.5 pr-3">
                          <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${r.trigger === "manual" ? "text-sky-300 bg-sky-500/15 border-sky-500/30" : "text-slate-300 bg-white/5 border-white/15"}`}>{r.trigger}</span>
                        </td>
                        <td className="py-1.5 pr-3 text-right">{r.candidates ?? 0}</td>
                        <td className="py-1.5 pr-3 text-right text-sky-300">{r.deduped ?? 0}</td>
                        <td className="py-1.5 pr-3 text-right text-indigo-300">{r.fetched ?? 0}</td>
                        <td className="py-1.5 pr-3 text-right text-emerald-300 font-600">{r.created ?? 0}</td>
                        <td className="py-1.5 pr-3 text-right text-teal-300">{r.merged ?? 0}</td>
                        <td className="py-1.5 pr-3 text-right text-amber-300">{r.skipped ?? 0}</td>
                        <td className={`py-1.5 text-right font-600 ${(r.errors ?? 0) > 0 ? "text-rose-300" : "text-slate-500"}`}>{r.errors ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-slate-500 mt-2">Every automatic (cron) and manual sync is logged here so nothing slips through silently. "Deduped" = already-seen emails whose bodies were never re-downloaded.</p>
            </>
          ) : (
            <div className="py-6 text-center text-sm text-slate-400"><Loader2 className="inline h-4 w-4 animate-spin mr-2" />Loading ingestion stats…</div>
          )}
        </div>
      )}

      {isAdmin && (
        <div className="rounded-lg border border-border bg-card p-5" data-testid="scrape-health">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-teal" />
              <h3 className="font-display text-lg font-600">Portal Scraper Watchdog</h3>
            </div>
            <button data-testid="scrape-refresh-btn" onClick={loadScrape}
              className="inline-flex items-center gap-1.5 rounded-md border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-slate-300 hover:bg-white/5 transition-colors">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(scrape?.portals || []).map((p) => {
              const st = p.status || "unknown";
              const cfg = {
                ok: { badge: "text-emerald-300 bg-emerald-500/15 border-emerald-500/30", label: "Healthy", Icon: ShieldCheck, iconColor: "text-emerald-400" },
                degraded: { badge: "text-amber-300 bg-amber-500/15 border-amber-500/30", label: "Degraded", Icon: AlertTriangle, iconColor: "text-amber-400" },
                alerting: { badge: "text-rose-300 bg-rose-500/15 border-rose-500/30", label: "Selector Broken", Icon: ShieldAlert, iconColor: "text-rose-400" },
                unknown: { badge: "text-slate-300 bg-white/5 border-white/15", label: "Not yet run", Icon: Activity, iconColor: "text-slate-400" },
              }[st] || {};
              const Icon = cfg.Icon || Activity;
              return (
                <div key={p.key} data-testid={`scrape-portal-${p.key}`} className={`rounded-md border p-3 ${st === "alerting" ? "border-rose-500/40 bg-rose-500/5" : st === "degraded" ? "border-amber-500/30 bg-amber-500/5" : "border-white/10 bg-white/5"}`}>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <Icon className={`h-4 w-4 shrink-0 ${cfg.iconColor}`} />
                      <span className="text-sm font-600 text-slate-100 truncate">{p.label}</span>
                    </div>
                    <span data-testid={`scrape-status-${p.key}`} className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${cfg.badge}`}>{cfg.label}</span>
                  </div>
                  <div className="text-[11px] text-slate-400 space-y-0.5">
                    {p.consecutive_missing > 0 && (
                      <div className="text-rose-300 font-semibold">{p.consecutive_missing} consecutive miss(es){scrape?.threshold ? ` · alerts at ${scrape.threshold}` : ""}</div>
                    )}
                    {p.last_ok_at && <div>Last healthy: {relTime(p.last_ok_at)}</div>}
                    {p.last_missing_at && st !== "ok" && <div>Last miss: {relTime(p.last_missing_at)}</div>}
                    {p.last_order_ref && <div className="font-mono truncate">Last order: {p.last_order_ref}</div>}
                    {st === "unknown" && <div>No Duval search has run since deploy.</div>}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-slate-500 mt-3">If a county changes its website and the search box disappears, admins are emailed after {scrape?.threshold || 3} consecutive misses so broken selectors get patched fast.</p>
        </div>
      )}

      <div className="rounded-lg border border-border bg-card p-5" data-testid="email-feed">
        <div className="flex items-center gap-2 mb-4">
          <Radio className="h-4 w-4 text-emerald-500" />
          <h3 className="font-display text-lg font-600">Monitored Inbox</h3>
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 px-2 py-0.5 text-[11px] font-semibold text-emerald-300">Live feed</span>
          <button data-testid="simulate-email-btn" onClick={simulateEmail} disabled={simulating}
            className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-white/15 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:bg-white/5 transition-colors disabled:opacity-50">
            {simulating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Simulate incoming email
          </button>
        </div>
        <div className="divide-y divide-white/10">
          {inbox.length === 0 && <div className="py-6 text-center text-sm text-slate-400">No incoming emails.</div>}
          {inbox.map((e) => (
            <div key={e.id} data-testid={`email-${e.id}`} className="flex items-start justify-between gap-4 py-3">
              <div className="flex items-start gap-3 min-w-0">
                <Mail className="h-4 w-4 text-slate-300 mt-1 shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-100 truncate">{e.subject}</div>
                  <div className="text-xs text-slate-400 font-mono">{e.from_addr}</div>
                  <div className="mt-1 flex items-center gap-3 text-xs text-slate-400">
                    <span className="flex items-center gap-1"><Paperclip className="h-3 w-3" /> {e.attachment_name}</span>
                    <span data-testid={`email-time-${e.id}`} className="text-slate-400">{relTime(e.received_at)}</span>
                  </div>
                </div>
              </div>
              <div className="shrink-0">
                {e.processed ? (
                  <button data-testid={`open-email-order-${e.id}`} onClick={() => navigate(`/orders/${e.order_id}`)} className="rounded-md border border-white/15 px-3 py-1.5 text-xs font-medium text-slate-300 hover:bg-white/5 transition-colors">
                    View Order
                  </button>
                ) : (
                  <button data-testid={`process-email-${e.id}`} onClick={() => processEmail(e.id)} disabled={processingId === e.id} className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800 transition-colors disabled:opacity-50">
                    {processingId === e.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                    {processingId === e.id ? "Parsing…" : "Process with AI"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: uploader / preview */}
        <div className="rounded-lg border border-border bg-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="h-4 w-4 text-slate-400" />
            <h3 className="font-display text-lg font-600">Attachment</h3>
          </div>

          <label
            data-testid="dropzone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files[0]); }}
            className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-white/15 bg-white/5 px-6 py-12 cursor-pointer hover:border-primary transition-colors duration-200"
          >
            <UploadCloud className="h-8 w-8 text-slate-400" />
            <p className="mt-3 text-sm font-medium text-slate-300">{file ? file.name : "Drag & drop or click to upload"}</p>
            <p className="text-xs text-slate-400 mt-1">PDF, DOCX, TXT or EML</p>
            <input data-testid="file-input" type="file" className="hidden" accept=".pdf,.docx,.txt,.eml" onChange={(e) => onFile(e.target.files[0])} />
          </label>

          <div className="mt-4 rounded-md border border-teal-500/20 bg-teal-500/10 px-4 py-3 text-xs text-slate-300">
            <Sparkles className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5 text-teal-400" /> AI extraction is <span className="text-teal-300 font-semibold">ON</span> (your Anthropic key). Drop a search package here and the Borrower, Property, County, Parcel & Client details fill automatically on the right for review — then click Create Order.
          </div>

          {file && (
            <button data-testid="reextract-ai-btn" onClick={() => parse()} disabled={parsing}
              className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-navy-2 transition-colors disabled:opacity-50">
              {parsing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} {parsing ? "Extracting…" : "Re-extract with AI"}
            </button>
          )}

          {result?.extracted_text && (
            <div className="mt-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Extracted Text</div>
              <pre className="max-h-56 overflow-auto rounded-md border border-white/10 bg-white/5 p-3 text-[11px] leading-relaxed text-slate-300 whitespace-pre-wrap">{result.extracted_text}</pre>
            </div>
          )}
        </div>

        {/* Right: extracted fields */}
        <div className="rounded-lg border border-border bg-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="h-4 w-4 text-blue-500" />
            <h3 className="font-display text-lg font-600">AI-Extracted Fields</h3>
          </div>

          {parsing && (
            <div className="space-y-3" data-testid="parse-skeleton">
              {[...Array(4)].map((_, i) => <div key={i} className="h-10 rounded-md bg-white/10 animate-pulse" />)}
            </div>
          )}

          {!parsing && !form && (
            <div className="rounded-md border border-dashed border-white/10 p-10 text-center text-sm text-slate-400">
              Extracted values will appear here for review.
            </div>
          )}

          {form && (
            <div className="space-y-4 animate-fade-up">
              <Field label="Order ID" testid="field-order-ref" value={form.order_ref} onChange={(v) => setForm({ ...form, order_ref: v })} />
              <Field label="Property Address" testid="field-address" value={form.property_address} onChange={(v) => setForm({ ...form, property_address: v })} />
              <div className="grid grid-cols-3 gap-3">
                <Field label="State" testid="field-state" value={form.state} onChange={(v) => setForm({ ...form, state: v })} />
                <Field label="County" testid="field-county" value={form.county} onChange={(v) => setForm({ ...form, county: v })} />
                <Field label="Zip" testid="field-zip" value={form.zip_code} onChange={(v) => setForm({ ...form, zip_code: v })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Borrower" testid="field-borrower" value={form.borrower} onChange={(v) => setForm({ ...form, borrower: v })} />
                <Field label="Parcel / Property ID" testid="field-parcel" value={form.parcel_id} onChange={(v) => setForm({ ...form, parcel_id: v })} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-300 mb-1 block">Company</label>
                <select data-testid="field-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="w-full rounded-md border border-white/15 px-3 py-2 text-sm focus:ring-2 focus:ring-teal focus:outline-none">
                  <option value="">— Select company —</option>
                  <option value="TitlePriority, LLC">TitlePriority, LLC</option>
                  <option value="YDealTitleServices, LLC">YDealTitleServices, LLC</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-300 mb-1 block">Client Group</label>
                <select data-testid="field-group" value={form.source_group} onChange={(e) => setForm({ ...form, source_group: e.target.value })}
                  className="w-full rounded-md border border-white/15 px-3 py-2 text-sm focus:ring-2 focus:ring-teal focus:outline-none">
                  <option value="">— Select group —</option>
                  <option value="orders@ydealtitleservices.com">orders@ydealtitleservices.com</option>
                  <option value="searches@ydealtitleservices.com">searches@ydealtitleservices.com</option>
                  <option value="orders@titlepriority.com">orders@titlepriority.com</option>
                  <option value="searches@titlepriority.com">searches@titlepriority.com</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-300 mb-1 block">Requested Product Type</label>
                <div className="flex items-center gap-2">
                  <select
                    data-testid="field-product"
                    value={form.product_type}
                    onChange={(e) => setForm({ ...form, product_type: e.target.value })}
                    className="flex-1 rounded-md border border-white/15 px-3 py-2 text-sm focus:ring-2 focus:ring-teal focus:outline-none"
                  >
                    {(PRODUCTS.includes(form.product_type) ? PRODUCTS : [form.product_type, ...PRODUCTS]).map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <ProductBadge product={form.product_type} />
                </div>
              </div>

              {/* Client PII — admin only */}
              {isAdmin ? (
                <div className="rounded-md border border-white/10 p-3 space-y-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Client Details (Admin only)</div>
                  <Field label="Client Name" testid="field-client-name" value={form.client_name} onChange={(v) => setForm({ ...form, client_name: v })} />
                  <Field label="Client Last Name" testid="field-client-last" value={form.client_last_name} onChange={(v) => setForm({ ...form, client_last_name: v })} />
                  <Field label="Client Email" testid="field-client-email" value={form.client_email} onChange={(v) => setForm({ ...form, client_email: v })} />
                  <Field label="Client Phone" testid="field-client-phone" value={form.client_phone} onChange={(v) => setForm({ ...form, client_phone: v })} />
                </div>
              ) : (
                <div data-testid="pii-locked" className="flex items-center gap-2 rounded-md border border-white/10 bg-white/10 p-3 text-sm text-slate-400">
                  <Lock className="h-4 w-4" /> Client contact details are captured but hidden from employees.
                </div>
              )}

              <button
                data-testid="create-order-btn"
                onClick={createOrder}
                disabled={creating}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-navy-2 transition-colors duration-200 disabled:opacity-50"
              >
                <CheckCircle2 className="h-4 w-4" /> {creating ? "Creating…" : "Create & Open Order"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-300 mb-1 block">{label}</label>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-white/15 px-3 py-2 text-sm focus:ring-2 focus:ring-teal focus:outline-none"
      />
    </div>
  );
}
